import type { Challenge } from '../data/types';
import type { RoadStop } from '../components/RoadMap';

/**
 * The geometry of the road in depth (components/DepthRoad.tsx): where a stop
 * stands on the screen for a camera standing `camera` stops along the road,
 * and which Mrs. Jennys stands beside it. Pure, so the landing's pre-drawn
 * copy in index.html and the app agree to the pixel.
 */

/** A done stop's print, if it has one in the darkroom (K37–K39). */
export const printOf = (s: RoadStop) => (s.state === 'done' ? s.print?.state : undefined);

/** Perspective: how many stops it takes for a stop to look half its size. */
const DEPTH = 2;
const Y_HORIZON = 24;
const Y_NEAR = 79;
/** how far a stop sits off the middle (percent of the width), at the nearest */
const SWAY = 26;
/** today's stop's size at the nearest, in container units */
const NEAR_SIZE = 21;
/** stops further than this are not drawn: the road goes on, dotted, to the goal */
export const WINDOW = 4.6;

export const r2 = (v: number) => Math.round(v * 100) / 100;
export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/** Where a stop `t` stops ahead of the camera sits on the screen, and how big it is (p = 1 at the nearest). */
export function project(t: number): { p: number; y: number } {
  const p = Math.min(3, 1 / (1 + Math.max(t, -1.4) / DEPTH));
  return { p, y: Y_HORIZON + (Y_NEAR - Y_HORIZON) * p };
}

export function place(i: number, camera: number) {
  const t = i - camera;
  const { p, y } = project(t);
  return { t, p, y, x: 50 + SWAY * p * (i % 2 === 0 ? -1 : 1), s: NEAR_SIZE * p };
}

/** Mrs. Jennys at a stop: the kind of making it is, and with friends where a chapter ends. */
const SPRITE: Record<string, string> = { ideas: 'ideas', words: 'words', draw: 'draw', voice: 'voice', photo: 'photo', open: 'imagine' };
export function spriteFor(c: Challenge, i: number, chapterEnd: boolean): string {
  if (chapterEnd) return 'friends';
  // most days are words days: every other one she is dreaming it up rather than writing it down
  if (c.tag === 'words' && i % 2 === 1) return 'imagine';
  return SPRITE[c.tag] ?? 'imagine';
}
export const spriteSrc = (name: string) => `/art/road/jennys-${name}.webp`;
export const GOAL_ART = '/art/road/goal-tree.webp';

export type Pt = [number, number];

/** One cubic Bézier per gap, through every point (Catmull-Rom), so each gap can be its own width. */
export function pieces(pts: Pt[]): string[] {
  const out: string[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    out.push(`M ${r2(p1[0])} ${r2(p1[1])} C ${r2(c1[0])} ${r2(c1[1])}, ${r2(c2[0])} ${r2(c2[1])}, ${r2(p2[0])} ${r2(p2[1])}`);
  }
  return out;
}


